import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    IAccount,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
} from '@modeso/types__aumico-lib-annualreport-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { AccountMappingTemplateStatus } from '../../../enums/account-mapping-template-status.enum';
import { DuplicateAccountHelper } from '../../../helpers/duplicateAccountHelper';
import { IAccountMappingDataMapped } from '../../../interfaces/IAccountMappingDataMapped.interface';
import { IDataGridDisplayAccount } from '../../../interfaces/data-grid-display-accounts.interface';
import { IUpdateAccount } from '../../../interfaces/update-account.interface';
import { AccountMappingEngine } from '../../../mappers/account-mapping-engin';
import { DataActions } from '../../data-grid/data-grid.component';
import { IGridColumn } from '../../data-grid/interfaces/IColumn.interface';
import { IDataGrid } from '../../data-grid/interfaces/IDataGrid.interface';

const debug = Debug('aumico:aumico-lib-templates-fe:AccountMappingComponent');

@UntilDestroy()
@Component({
    selector: 'templates-account-mapping-child-form',
    templateUrl: 'account-mapping-child-form.component.html',
    styleUrls: ['account-mapping-child-form.component.scss'],
})
export class AccountMappingChildComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input()
    languages: string[] = ['de-ch'];

    @Input()
    currency = '';

    columns: IGridColumn[] = [];
    summarizedAccountError: string;

    @Input()
    set hasPreviousData(value: boolean) {
        if (value != null && this._engine) {
            this._hasPreviousData = value;
            this.prepareData();
            this.refreshSubscriptions();
        }
    }

    get hasPreviousData() {
        return this._hasPreviousData;
    }

    @Input() isCashFlowTypeForFliUsed: boolean | undefined;
    @Input() businessModel: string;
    @Input() reportLocale: string;
    @Input() disableEditingPreviousYearValue = false;

    @Input() supportOperation = false;

    @Input() type: string;
    _dataSource: IAccount[];
    subscriptions: Subscription[] = [];
    get rawAccountsDataSource() {
        const unselectedAccounts = this._engine ? [...this._engine.unSelectedAccounts] : [];
        return this.mapAccountsToGridDisplayData(unselectedAccounts);
    }

    @Output()
    dataChangedEventEmitter = new EventEmitter<IAccountMappingDataMapped>();
    @Output()
    addOrUpdateAccountInActiveReport = new EventEmitter<IUpdateAccount>();
    @Output() fliOrGroupDeleted: EventEmitter<IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI> =
        new EventEmitter<IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI>();

    rawAccountsGridUiData: IDataGrid = {} as IDataGrid;
    private _engine: AccountMappingEngine;
    private _hasPreviousData: boolean;

    showSummarizeAccountsModal = false;
    showUnsummarizeConfirmModal = false;
    accountToUnsummarize: IDataGridDisplayAccount | null = null;
    accountsForSummary: IDataGridDisplayAccount[] = [];
    summarizedAccount: IDataGridDisplayAccount | null = null;

    @Input()
    public get engine(): AccountMappingEngine {
        return this._engine;
    }
    public set engine(value: AccountMappingEngine) {
        this._engine = value;
        this.prepareData();
        this.refreshSubscriptions();
    }

    @Input() cashAndCashFli: string | undefined;

    @Input() profitOfTheYearFliName: string | undefined;

    @Input() errorInProfitFli: boolean | undefined;

    @Input() isAutomaticTransferOfAnnualResultEnabled: boolean | undefined;

    constructor(private localizedTexts: I18NextService, private readonly el: ElementRef) {
        this.applyLocalizations();
        this.prepareData = this.prepareData.bind(this);
    }

    private resizeObserver: ResizeObserver | undefined;
    private intersectionObserver: IntersectionObserver | undefined;

    @HostListener('window:resize')
    onResize(): void {
        this.updateColumnHeight();
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.updateColumnHeight());
        this.resizeObserver.observe(this.el.nativeElement);

        // ResizeObserver never re-fires when an inactive Material tab becomes visible
        // (translate-based show/hide doesn't change the element's size). Use
        // IntersectionObserver to catch the moment the IS panel enters the viewport.
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    this.updateColumnHeight();
                }
            },
            { threshold: 0 },
        );
        this.intersectionObserver.observe(this.el.nativeElement);
    }

    private updateColumnHeight(): void {
        const FOOTER_HEIGHT = 64;
        const col: HTMLElement | null = this.el.nativeElement.querySelector('.column-height');
        if (!col) return;
        const top = col.getBoundingClientRect().top;
        if (top <= 0) return; // element not yet laid out — skip until ResizeObserver fires again
        const height = window.innerHeight - top - FOOTER_HEIGHT;
        this.el.nativeElement.style.setProperty('--column-height', `${height}px`);
    }

    ngOnInit(): void {
        this.refreshSubscriptions();
    }

    private applyLocalizations(): void {
        if (this._engine != null) {
            this.prepareData();
        }
    }

    private refreshSubscriptions() {
        this.unSubscribeAll();
        this.subscriptions = [];
        if (this.engine) {
            this.subscriptions.push(
                this.engine.stateChangedSubject.subscribe(() => {
                    this.updateDataGridSource();
                    this.emitChangeEvent();
                }),
            );
        }
    }
    emitChangeEvent() {
        let dataToEmit: IAccountMappingDataMapped = {
            accounts: this.engine.getAllAccounts(),
            template: this.engine.getMappedTemplate(),
            status: AccountMappingTemplateStatus.MAPPED,
        };
        if (this.type === TemplateType.BALANCE) {
            dataToEmit.balanceSwitchAccounts = this.engine.getBalanceSwitchAccounts();
        }
        this.dataChangedEventEmitter.emit(dataToEmit);
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.unSubscribeAll();
    }

    private unSubscribeAll() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions = [];
    }

    onRowsSelected(rows: IDataGridDisplayAccount[]) {
        debug('selected row', rows);
        const accounts = this.mapGridDisplayAccountsToMappingAccounts(rows);
        this.engine.setDraggedAccountsInIndices(accounts);
    }

    onDeletedRows(rows: IDataGridDisplayAccount[]) {
        debug('deleted row', rows);
        const accounts = this.mapGridDisplayAccountsToMappingAccounts(rows);
        this.engine.deleteAccounts(accounts);
    }

    private prepareData(): void {
        const dates = this._engine ? this._engine.getDates() : ({} as ReturnType<AccountMappingEngine['getDates']>);
        const endDate = dates.endDate;
        const startDate = dates.startDate;
        this.columns = [
            {
                name: this.localizedTexts.t('aumico_templates_account_mapping_child_form_number') as string,

                nameType: 'string',
                dataType: 'string',
                dataField: 'accountNumber',
                excludedFromSearch: false,
                matchInitials: true,
            },
            {
                name: this.localizedTexts.t('aumico_templates_account_mapping_child_form_name') as string,

                nameType: 'string',
                dataType: 'string',
                dataField: 'displayName',
                excludedFromSearch: false,
                matchInitials: true,
                duplicateIcon: true,
            },
            {
                name: endDate,
                nameType: 'date',
                dataType: 'number',
                dataField: 'closingBalance',
                excludedFromSearch: true,
            },
        ];

        if (this._hasPreviousData) {
            const startDateOption: IGridColumn = {
                name: startDate,
                nameType: 'date',
                dataType: 'number',
                dataField: 'openingBalance',
                excludedFromSearch: true,
            };

            this.columns.push(startDateOption);
        }
        this.updateDataGridSource();
    }

    private updateDataGridSource(): void {
        this.rawAccountsGridUiData = {
            columns: this.columns,
            allowSearch: false,
            allowSelection: true,
            gridEditing: { allowDeleting: true, allowAdding: true, allowUpdating: true },
            dataSource: this.rawAccountsDataSource,
        };
    }

    onAddOrUpdateActiveReport(updatedAccountData: IUpdateAccount): void {
        this.addOrUpdateAccountInActiveReport.emit(updatedAccountData);
    }

    onAction(event: { eventType: DataActions; data: object }) {
        if (event.eventType === DataActions.DUPLICATE_ACCOUNT) {
            this.onDuplicateIconClick(event.data as IDataGridDisplayAccount);
        } else if (event.eventType === DataActions.UNSUMMARIZE_ACCOUNT) {
            this.onUnsummarizedClick(event.data as IDataGridDisplayAccount);
        }
    }

    onDuplicateIconClick(row: IDataGridDisplayAccount) {
        const accountsWithSameNumber = this.rawAccountsDataSource.filter(
            (acc) => acc.accountNumber === row.accountNumber,
        );
        this.accountsForSummary = accountsWithSameNumber;

        if (accountsWithSameNumber.length > 1) {
            const closingBalance = accountsWithSameNumber.reduce((sum, acc) => sum + (acc.closingBalance || 0), 0);
            const openingBalance = accountsWithSameNumber.reduce((sum, acc) => sum + (acc.openingBalance || 0), 0);

            this.summarizedAccount = {
                ...accountsWithSameNumber[0], // template for other properties
                closingBalance,
                openingBalance,
            };
        } else {
            this.summarizedAccount = accountsWithSameNumber.length > 0 ? { ...accountsWithSameNumber[0] } : null;
        }

        this.showSummarizeAccountsModal = true;
    }

    onUnsummarizedClick(row: IDataGridDisplayAccount) {
        this.accountToUnsummarize = row;
        this.showUnsummarizeConfirmModal = true;
    }

    onConfirmUnsummarize(): void {
        console.log('Unsummarizing account:', this.accountToUnsummarize);
        if (this.accountToUnsummarize?.accountNumber) {
            this.engine.unsummarizeAccount(this.accountToUnsummarize.accountNumber);
        }
        this.showUnsummarizeConfirmModal = false;
        this.accountToUnsummarize = null;
    }

    onCancelUnsummarize(): void {
        this.showUnsummarizeConfirmModal = false;
        this.accountToUnsummarize = null;
    }

    onCloseSummaryModal(): void {
        this.showSummarizeAccountsModal = false;
        this.accountsForSummary = [];
        this.summarizedAccount = null;
    }

    onSummarizeAccounts(): void {
        if (this.summarizedAccount?.accountNumber) {
            try {
                this.engine.summarizeAccounts(this.summarizedAccount.accountNumber);

            } catch (error) {
                if (error instanceof Error && error.message === 'Cannot summarize accounts because at least one is already mapped.') {
                    this.summarizedAccountError = this.localizedTexts.t('aumico_templates_account_mapping_child_form_summarize_error_mapped_account') as string;
                }
            }
        }
        this.onCloseSummaryModal();
    }

    onConfirmError(): void {
        this.summarizedAccountError = undefined;
    }

    public onFliOrGroupDeleted(deletedItem: IAccountMappingTemplateGroup | IAccountMappingTemplateGroupItemFLI): void {
        this.fliOrGroupDeleted.emit(deletedItem);
    }

    private mapAccountsToGridDisplayData(accounts: IAccount[]): IDataGridDisplayAccount[] {
        const clonedAccounts: IAccount[] = cloneDeep<IAccount[]>(accounts);

        // 1. sort accounts by account number
        // 2. use DuplicateAccountHelper to find duplicate account numbers 
        // 3. add icons to duplicate accounts
        const sortedAccounts = clonedAccounts.filter((acc) => acc != null).sort((a, b) => {
            if (a.accountNumber && b.accountNumber) {
                return a.accountNumber.localeCompare(b.accountNumber);
            } else if (a.accountNumber) {
                return -1;
            } else if (b.accountNumber) {
                return 1;
            } else {
                return 0;
            }
        });

        const accountsWithShareholderInformation = sortedAccounts.map((acc) => {
            return {
                ...acc,
                displayName:
                    acc.shareholder != null && acc.shareholder !== '' ? acc.name + ' - ' + acc.shareholder : acc.name,
            };
        });

        const accountsWithDuplicateInfo: IDataGridDisplayAccount[] =
            DuplicateAccountHelper.markDuplicateAccounts(accountsWithShareholderInformation);

        return accountsWithDuplicateInfo;
    }

    private mapGridDisplayAccountsToMappingAccounts(gridAccounts: IDataGridDisplayAccount[]): IAccount[] {
        return gridAccounts.map((gridAccount) => {
            const newAccount = cloneDeep<IAccount>(gridAccount);
            delete newAccount.displayName;
            return newAccount;
        });
    }
}
